import React, { useEffect, useState } from 'react';
import { useI18n } from '../../i18n/i18nContext';
import { Activity, Users, Truck, ShoppingBag, Globe, BarChart2 } from 'lucide-react';
// Import git-info.json using baseUrl (src/git-info.json)
import gitInfo from 'git-info.json';

interface FooterProps {
    footerStats?: any;
    t?: (key: string) => string;
}

interface FooterStats {
    drivers: { online: number; total: number };
    customers: { online: number; total: number };
    admins: { online: number; total: number };
    support: { online: number; total: number };
    ordersCompletedToday: number;
    activeOrders: number;
    countriesReached: number;
    systemLoad: { rpm: number; status: string };
}

const Footer: React.FC<FooterProps> = ({ footerStats }) => {
    const { t } = useI18n();
    // Version from git-info.json (generated at build time with correct version via baseUrl import)
    const version = (gitInfo as any).version || '1.0.0';

    const [stats, setStats] = useState<FooterStats | null>(footerStats || null);

    useEffect(() => {
        // If footerStats is provided via prop, use it and skip fetching
        if (footerStats) {
            setStats(footerStats);
            return;
        }

        // Only fetch if no footerStats prop is provided
        const fetchStats = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
                const response = await fetch(`${apiUrl}/stats/footer`);
                if (response.ok) {
                    const data = await response.json();
                    setStats(data);
                }
            } catch (error) {
                console.error('Failed to fetch footer stats:', error);
            }
        };

        fetchStats();
        // Poll every 30 seconds only if not using prop
        const interval = setInterval(fetchStats, 30000);
        return () => clearInterval(interval);
    }, [footerStats]);

    const StatItem = ({ label, value, icon: Icon }: any) => (
        <div style={{ display: 'flex', alignItems: 'center', margin: '0 12px', fontSize: '0.75rem', fontFamily: 'monospace' }}>
            <Icon size={14} style={{ marginRight: '6px', color: '#00FF41' }} />
            <span style={{ marginRight: '6px', fontWeight: 600, color: '#00FF41' }}>{value}</span>
            <span style={{ color: '#008F11' }}>{label}</span>
        </div>
    );

    return (
        <footer className="site-footer" style={{
            padding: '1.5rem 1rem',
            textAlign: 'center',
            fontSize: '0.75rem',
            color: '#008F11',
            borderTop: '1px solid #003300',
            background: '#000000',
            fontFamily: 'monospace'
        }}>
            {stats && (
                <div style={{
                    maxWidth: '80rem',
                    margin: '0 auto 1rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: '12px 24px',
                    borderBottom: '1px solid #003300',
                    paddingBottom: '1rem'
                }}>
                    <StatItem icon={Truck} label={t('footer.onlineDrivers')} value={`${stats.drivers?.online ?? 0}/${stats.drivers?.total ?? 0}`} />
                    <StatItem icon={Users} label={t('footer.onlineCustomers')} value={`${stats.customers?.online ?? 0}/${stats.customers?.total ?? 0}`} />
                    {stats.support && <StatItem icon={Users} label={t('footer.onlineSupport')} value={`${stats.support.online}/${stats.support.total}`} />}
                    {stats.admins && <StatItem icon={Users} label={t('footer.onlineAdmins')} value={`${stats.admins.online}/${stats.admins.total}`} />}
                    <StatItem icon={ShoppingBag} label={t('footer.ordersToday')} value={stats.ordersCompletedToday ?? 0} />
                    <StatItem icon={Activity} label={t('footer.activeOrders')} value={stats.activeOrders ?? 0} />
                    <StatItem icon={Globe} label={t('footer.countries')} value={stats.countriesReached ?? 0} />
                    {stats.systemLoad && <StatItem icon={BarChart2} label={t('footer.systemLoad')} value={`${stats.systemLoad.status} (${stats.systemLoad.rpm} rpm)`} />}
                </div>
            )}
            <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
                <p style={{ margin: 0, color: '#004400' }}>
                    v{version} | <a href="https://amrlotfy.et3am.com" style={{ color: '#008F11', textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">Amr Lotfy</a> | MIT License
                </p>
            </div>
        </footer>
    );
};

export default Footer;
